import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSeo } from '@/hooks/useSeo';
import { categories } from '@/mocks/categories';
import { siteInfo, stats, testimonials } from '@/mocks/site';

const principles = [
  {
    icon: 'ri-shield-check-line',
    title: '只写可查证的内容',
    text: '所有事件均整理自当事人声明、官方公告与主流媒体报道，不采用无法核实的匿名私信。',
    accent: 'text-primary-600 bg-primary-50 border-primary-200',
  },
  {
    icon: 'ri-chat-3-line',
    title: '双方说法并列呈现',
    text: '涉及争议的事件，我们会把各方回应按时间顺序完整列出，而不是只保留传播度更高的那一方。',
    accent: 'text-accent-700 bg-accent-50 border-accent-200',
  },
  {
    icon: 'ri-time-line',
    title: '持续更新而非重发',
    text: '热点会继续发展，我们选择在原事件页追加节点，让你回看时能看到完整过程而不是零散碎片。',
    accent: 'text-secondary-800 bg-secondary-50 border-secondary-200',
  },
  {
    icon: 'ri-emotion-line',
    title: '不消费当事人',
    text: '我们讨论事件与规则，不讨论未经证实的私人生活细节，也不对当事人做人身评价。',
    accent: 'text-primary-700 bg-primary-50 border-primary-200',
  },
];

const timeline = [
  { year: '2021', text: '站点以个人博客形式开始记录网络热议话题，仅做简单的信息汇总。' },
  { year: '2023', text: '引入时间线整理方式，把同一事件的多条更新合并到一个页面。' },
  { year: '2025', text: '正式确立六大内容栏目，开始按分类长期维护更新节奏。' },
  { year: '2026', text: '上线实时热榜与订阅服务，读者可以在固定时段收到当日热点摘要。' },
];

export default function AboutPage() {
  const jsonLd = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const basePath = typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__.replace(/\/$/, '') : '';
    const siteUrl = `${origin}${basePath}`;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于我们 - 17c吃瓜',
        url: `${siteUrl}/about`,
        description: siteInfo.description,
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteInfo.name,
        url: `${siteUrl}/`,
        description: siteInfo.description,
        email: siteInfo.email,
        slogan: siteInfo.tagline,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
    ];
  }, []);

  useSeo({
    title: '关于我们 - 网站定位与内容原则 | 17c吃瓜',
    description:
      '了解17c吃瓜的定位、发展历程与内容原则。我们聚焦今日热门事件与全网热议话题，坚持只整理可查证的公开信息，把碎片传闻梳理成完整脉络。',
    keywords: ['17c吃瓜', '关于我们', '内容原则', '网站定位', '今日热门事件'],
    path: '/about',
    jsonLd,
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full">
        <div className="w-full px-4 md:px-8 pt-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
        </div>

        <section className="w-full px-4 md:px-8 py-6 md:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs text-primary-700 bg-primary-50 border border-primary-200 px-2.5 py-1 rounded-full">
                <i className="ri-building-2-line"></i>
                关于本站
              </span>
              <h1 className="mt-3 font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight">
                把碎片传闻，梳理成能读懂的事件
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                {siteInfo.name} 是一个专注于网络热点整理的内容站点。我们不做爆料首发，也不做情绪出口，只做一件相对枯燥但有价值的事情：
                把同一件事在不同渠道的碎片信息，按时间顺序重新拼起来，并明确标出哪些是已确认信息、哪些仍属网传。
              </p>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                定位很简单——当你刷到一条让人困惑的热搜，能在三分钟内看懂它为什么发生、现在到哪一步了。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/#hot"
                  className="px-5 py-2.5 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看今日热榜
                </Link>
                <Link
                  to="/#subscribe"
                  className="px-5 py-2.5 text-sm rounded-md border border-background-300 text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  订阅热点更新
                </Link>
              </div>
            </div>
            <div className="w-full h-56 md:h-80 rounded-lg overflow-hidden border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20interconnected%20nodes%20and%20curved%20timeline%20paths%20with%20soft%20film%20grain%2C%20warm%20coral%20red%20and%20amber%20gradient%20tones%2C%20minimal%20geometric%20composition%2C%20muted%20cream%20background%2C%20magazine%20aesthetic%2C%20high%20detail&width=1200&height=840&seq=gua-about-01&orientation=landscape"
                alt="17c吃瓜网站定位与内容整理方式示意图"
                title="17c吃瓜 · 把碎片传闻梳理成完整脉络"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="w-full px-4 md:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {stats.map((item) => (
                <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 px-4 py-5 text-center">
                  <p className="font-heading text-2xl md:text-3xl font-black text-primary-600">{item.value}</p>
                  <p className="mt-1.5 text-xs md:text-sm text-foreground-600">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">四条内容原则</h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
            原则不是为了显得正规，而是为了让我们在面对流量诱惑时知道该守住什么。
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {principles.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className={`w-10 h-10 flex items-center justify-center rounded-md border ${item.accent}`}>
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="w-full px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-8 lg:gap-12">
              <div>
                <span className="inline-flex items-center gap-1.5 text-xs text-accent-800 bg-accent-50 border border-accent-200 px-2.5 py-1 rounded-full">
                  <i className="ri-route-line"></i>
                  发展历程
                </span>
                <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-tight">
                  一路做减法
                </h2>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  从个人记录到固定栏目，中间删掉了很多看起来热闹但并不解决问题的东西。
                </p>
              </div>
              <ol className="relative border-l-2 border-background-300 pl-6 space-y-6">
                {timeline.map((item) => (
                  <li key={item.year} className="relative">
                    <span className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full bg-accent-500 border-2 border-background-100"></span>
                    <p className="font-heading text-base font-bold text-accent-800">{item.year}</p>
                    <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{item.text}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">读者口碑</h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
            长期读者的真实反馈，也是我们调整栏目和排版的主要依据。
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map((item) => (
              <figure key={item.name} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                <i className="ri-double-quotes-l text-2xl text-primary-400"></i>
                <blockquote className="mt-3 flex-1 text-sm text-foreground-700 leading-relaxed">
                  {item.content}
                </blockquote>
                <figcaption className="mt-4 pt-4 border-t border-background-100 flex items-center gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-800 font-bold text-sm">
                    {item.name.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block text-sm text-foreground-900">{item.name}</span>
                    <span className="block text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="w-full px-4 md:px-8">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">联系我们</h2>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                    内容勘误、线索投稿、栏目建议都欢迎来信。我们只处理能够提供公开可查依据的线索，
                    其余类型的信息无法安排跟进，还请理解。
                  </p>
                  <ul className="mt-5 space-y-3">
                    <li className="flex items-center gap-3 text-sm text-foreground-700">
                      <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                        <i className="ri-mail-line"></i>
                      </span>
                      {siteInfo.email}
                    </li>
                    <li className="flex items-center gap-3 text-sm text-foreground-700">
                      <span className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-50 text-accent-700">
                        <i className="ri-time-line"></i>
                      </span>
                      投稿处理时间：工作日内 48 小时
                    </li>
                    <li className="flex items-center gap-3 text-sm text-foreground-700">
                      <span className="w-9 h-9 flex items-center justify-center rounded-md bg-secondary-50 text-secondary-800">
                        <i className="ri-chat-check-line"></i>
                      </span>
                      勘误说明会更新在原事件页的时间线中
                    </li>
                  </ul>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                  <h3 className="font-heading text-lg font-bold text-foreground-950">先订阅，再投稿</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                    通过订阅表单留下邮箱并勾选关注方向，编辑会在核实线索后优先回复订阅读者。
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className="px-3 py-1.5 text-xs rounded-full bg-background-50 text-foreground-600 border border-background-300 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                  <Link
                    to="/#subscribe"
                    className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-mail-send-line"></i>
                    前往订阅表单
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}